import { Card, Grid, Icon, Input, Link, Select, Spinner } from '@components/common'
import { useListFoldersQuery, useGetAssetIDsQuery } from '@api'
import { ReactSelectOptions } from '@components/common/Select/Select.types'
import { Controller, useFormContext } from 'react-hook-form'
import { AllAssetDetailFields, Asset, FieldLabels } from '@api/models'
import { useFormFieldProps } from '@hooks'
import { isNullOrUndefined } from '@utils/basic'
import { IUserGrantProps } from '@components/Auth/UserRole'
import AssetOrganizationLoading from './AssetOrganizationLoading'
import { useEffect } from 'react'

interface Props extends IUserGrantProps {
  fields: FieldLabels
  isLoadingInfo: boolean
  isAssetDisposedOrPendingDisposal: boolean
  asset: Asset
}

const AssetOrganization = ({
  fields,
  isLoadingInfo,
  isAssetDisposedOrPendingDisposal,
  asset,
  canEdit,
}: Props) => {
  const {
    register,
    formState: { errors, isSubmitting },
    control,
    setValue,
    watch,
  } = useFormContext<AllAssetDetailFields>()

  const {
    data: folders,
    isLoading: isLoadingFolders,
    isSuccess: isSuccessGettingFolders,
  } = useListFoldersQuery()

  const {
    data: assetIDs,
    isLoading: isLoadingAssetIDs,
    isSuccess: isSuccessGettingAssetIDs,
  } = useGetAssetIDsQuery({
    assetCompanyID: asset.AssetCompanyID
  }, {
    //added a skip incase no valid company ID
    skip: !asset.AssetCompanyID
  })
 
  const { getMultiSelectProps, getInputProps, getSelectProps } = useFormFieldProps<AllAssetDetailFields>({
    isSubmitting,
    errors: errors as any,
    fields,
    register,
  })

  // Watch values for debugging
  const leaseValue = watch('AS_LEASE')
  const parentAssetValue = watch('AS_PAR_ASS')


  //added useEffect for if the lease ID exists in the asset but not in the form
  useEffect(() => {
    if (asset?.AS_LEASE && !isLoadingInfo && !isSubmitting) {
      setValue('AS_LEASE', asset.AS_LEASE, { shouldValidate: true });
    }
  }, [asset, isLoadingInfo, setValue, isSubmitting]);

  // Prepare options arrays
  const tagOptions: ReactSelectOptions = []
  if (isSuccessGettingFolders && folders) {
    for (const folder of folders) {
      tagOptions.push({
        label: folder.Name,
        value: folder.Key,
      })
    }
  }

  const parentIDOptions: ReactSelectOptions = []
  if (isSuccessGettingAssetIDs && assetIDs) {
    //NEW: got rid of filter to prevent circular references
    for (const assetID of assetIDs) {
      parentIDOptions.push({
        label: assetID,
        value: assetID,
      })
    }
  }

  const isLoadingInfoOrFolders = isLoadingInfo || isLoadingFolders || isLoadingAssetIDs

  const handleLeaseChange = (value) => {
    // Ensure we're always saving a string
    setValue('AS_LEASE', value || null, { shouldValidate: true });
  };

  return (
    <Card title={'Asset organization'}>
      {isLoadingInfoOrFolders && <AssetOrganizationLoading />}

      {!isLoadingInfoOrFolders && (
        <Grid>
          <Grid.Item s={6}>
            <Controller
              control={control}
              name="AS_PAR_ASS"
              render={({ field }) => (
                <Select
                  {...getSelectProps('AS_PAR_ASS', field, parentIDOptions)}
                  isSearchable
                  readOnly={!canEdit || isAssetDisposedOrPendingDisposal}
                  helper={
                    !isNullOrUndefined(asset.AS_PAR_ASS) ? (
                      <Link
                        to={`/assets/${asset.AssetCompanyID}/${asset.AS_PAR_ASS}/details`}
                        external
                        icon={<Icon name="ExternalLink" />}
                      >
                        View parent asset
                      </Link>
                    ) : (
                      ''
                    )
                  }
                />
              )}
            />          
          </Grid.Item>
          <Grid.Item s={6}>
            {/* added a controller */ }
            <Controller
              control={control}
              name="AS_LEASE"
              render={({ field }) => (
                <Input
                  label={fields['AS_LEASE']}
                  value={field.value || ''}
                  onChange={(e) => {
                    field.onChange(e);
                    handleLeaseChange(e.target.value);
                  }}
                  onBlur={field.onBlur}
                  error={!!errors.AS_LEASE?.message}
                  readOnly={!canEdit || isAssetDisposedOrPendingDisposal}
                />
              )}
            />
          </Grid.Item>
          <Grid.Item>
            <Controller
              control={control}
              name="tags"
              render={({ field }) => (
                <Select
                  {...getMultiSelectProps('tags', field, tagOptions)}
                  type="multi"
                  isSearchable
                  placeholder="Search for tags..."
                  label="Tags/folders"
                  disabled={!canEdit || isAssetDisposedOrPendingDisposal}
                />
              )}
            />
          </Grid.Item>
        </Grid>
      )}      
      {Object.keys(errors).length > 0 && (
        <div style={{ color: 'red', marginTop: '10px' }}>
          {Object.entries(errors).map(([key, error]) => (
            <div key={key}>
              Error in {fields[key] || key}: 
              {Array.isArray(error) 
                ? error.map((e, index) => <span key={index}>{e.message}</span>) 
                : error.message}
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

export default AssetOrganization